/**
 * EditableFieldValue - Editable input component for Filevine field values
 *
 * Renders the appropriate input based on field type, respecting field configs.
 */

import { format } from 'date-fns';
import { DollarSign, Percent, X } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { CustomFieldType } from '@/services/api/entitySyncService';
import type { FilevineSectionFieldConfigSchema } from '@/services/api/generated/entitySyncService';

import type { FilevineSectionFieldValue } from '../utils/fieldEditors';
import { isFieldEditable } from '../utils/fieldEditors';
import { FieldValue } from '../utils/fieldRenderers';

export interface EditableFieldValueProps {
  /** Current field value */
  value: unknown;
  /** Field type from CustomFieldType enum */
  fieldType: number;
  /** Full field configuration */
  fieldConfig: FilevineSectionFieldConfigSchema;
  /** Callback when value changes */
  onChange: (value: FilevineSectionFieldValue) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Text input for TEXT fields
 */
const TextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <Input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={cn('h-8 text-sm', className)}
  />
);

/**
 * Textarea for MULTILINE_TEXT fields
 */
const MultilineTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <Textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    rows={3}
    className={cn('text-sm resize-none', className)}
  />
);

/**
 * Number input for INTEGER fields
 */
const IntegerEditor: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <Input
    type="number"
    value={value ?? ''}
    onChange={(e) => {
      const val = e.target.value;
      onChange(val === '' ? null : parseInt(val, 10));
    }}
    disabled={disabled}
    className={cn('h-8 text-sm', className)}
  />
);

/**
 * Currency input with $ prefix for CURRENCY fields
 */
const CurrencyEditor: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <div className={cn('relative', className)}>
    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
    <Input
      type="number"
      step="0.01"
      value={value ?? ''}
      onChange={(e) => {
        const val = e.target.value;
        onChange(val === '' ? null : parseFloat(val));
      }}
      disabled={disabled}
      className="h-8 text-sm pl-7"
    />
  </div>
);

/**
 * Percentage input with % suffix for PERCENTAGE fields
 */
const PercentageEditor: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <div className={cn('relative', className)}>
    <Input
      type="number"
      step="0.1"
      value={value ?? ''}
      onChange={(e) => {
        const val = e.target.value;
        onChange(val === '' ? null : parseFloat(val));
      }}
      disabled={disabled}
      className="h-8 text-sm pr-7"
    />
    <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
  </div>
);

/**
 * Date picker for DATE and DEADLINE fields
 */
const DateEditor: React.FC<{
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <div
    className={cn(
      'flex h-8 items-center rounded-md border border-input px-2',
      className,
    )}
  >
    <DatePicker
      date={value}
      onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd') : null)}
      disabled={disabled}
      placeholder="Select date"
    />
  </div>
);

/**
 * Datetime picker for DATETIME fields
 */
const DateTimeEditor: React.FC<{
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => {
  // Parse existing datetime or default to empty
  const dateValue = value ? value.split('T')[0] : null;
  const timeValue = value ? value.split('T')[1]?.slice(0, 5) : '';

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      onChange(null);
      return;
    }
    const dateStr = format(date, 'yyyy-MM-dd');
    onChange(timeValue ? `${dateStr}T${timeValue}:00` : `${dateStr}T00:00:00`);
  };

  const handleTimeChange = (time: string) => {
    if (!dateValue) return;
    onChange(time ? `${dateValue}T${time}:00` : `${dateValue}T00:00:00`);
  };

  return (
    <div className={cn('flex gap-2', className)}>
      <div className="flex h-8 flex-1 items-center rounded-md border border-input px-2">
        <DatePicker
          date={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          placeholder="Select date"
        />
      </div>
      <Input
        type="time"
        value={timeValue}
        onChange={(e) => handleTimeChange(e.target.value)}
        disabled={disabled}
        className="h-8 w-28 text-sm"
      />
    </div>
  );
};

/**
 * Checkbox for BOOLEAN fields
 */
const BooleanEditor: React.FC<{
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <div className={cn('flex items-center gap-2', className)}>
    <Checkbox
      checked={value}
      onCheckedChange={(checked) => onChange(checked === true)}
      disabled={disabled}
    />
    <span className="text-sm text-zinc-600">{value ? 'Yes' : 'No'}</span>
  </div>
);

/**
 * Select dropdown for DROPDOWN fields
 */
const DropdownEditor: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, options, onChange, disabled, className }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className={cn('h-8 text-sm', className)}>
      <SelectValue placeholder="Select..." />
    </SelectTrigger>
    <SelectContent>
      {options.map((option) => (
        <SelectItem key={option} value={option}>
          {option}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Tag input for STRING_LIST fields (free-form text tags)
 */
const StringListEditor: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => {
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-zinc-300"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      <Input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type and press Enter..."
        disabled={disabled}
        className="h-8 text-sm"
      />
    </div>
  );
};

/**
 * Tag input with autocomplete for MULTI_SELECT fields
 */
const MultiSelectEditor: React.FC<{
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, options, onChange, disabled, className }) => {
  const availableOptions = options.filter((opt) => !value.includes(opt));

  const addOption = (option: string) => {
    if (!value.includes(option)) {
      onChange([...value, option]);
    }
  };

  const removeOption = (optionToRemove: string) => {
    onChange(value.filter((opt) => opt !== optionToRemove));
  };

  return (
    <div className={cn('space-y-2', className)}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((opt) => (
            <Badge
              key={opt}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {opt}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeOption(opt)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-zinc-300"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      {availableOptions.length > 0 && (
        <Select value="" onValueChange={addOption} disabled={disabled}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="Add option..." />
          </SelectTrigger>
          <SelectContent>
            {availableOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

/**
 * Main EditableFieldValue component
 */
export const EditableFieldValue: React.FC<EditableFieldValueProps> = ({
  value,
  fieldType,
  fieldConfig,
  onChange,
  disabled = false,
  className,
}) => {
  // If field type is not editable, fall back to read-only display
  if (!isFieldEditable(fieldType)) {
    return <FieldValue value={value} fieldType={fieldType} />;
  }

  switch (fieldType) {
    case CustomFieldType.TEXT:
      return (
        <TextEditor
          value={(value as string) ?? ''}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.MULTILINE_TEXT:
      return (
        <MultilineTextEditor
          value={(value as string) ?? ''}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.INTEGER:
      return (
        <IntegerEditor
          value={value as number | null}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.CURRENCY:
      return (
        <CurrencyEditor
          value={value as number | null}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.PERCENTAGE:
      return (
        <PercentageEditor
          value={value as number | null}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.DATE:
    case CustomFieldType.DEADLINE:
      return (
        <DateEditor
          value={value as string | null}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.DATETIME:
      return (
        <DateTimeEditor
          value={value as string | null}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.BOOLEAN:
      return (
        <BooleanEditor
          value={(value as boolean) ?? false}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.DROPDOWN:
      return (
        <DropdownEditor
          value={(value as string) ?? ''}
          options={fieldConfig.dropdown_items ?? []}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.STRING_LIST:
      return (
        <StringListEditor
          value={(value as string[]) ?? []}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    case CustomFieldType.MULTI_SELECT:
      return (
        <MultiSelectEditor
          value={(value as string[]) ?? []}
          options={fieldConfig.dropdown_items ?? []}
          onChange={onChange}
          disabled={disabled}
          className={className}
        />
      );

    default:
      // For unsupported types, show read-only
      return <FieldValue value={value} fieldType={fieldType} />;
  }
};

export default EditableFieldValue;
